import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, File, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Model3DUploaderProps {
  onModelUploaded?: (url: string) => void;
}

export const Model3DUploader = ({ onModelUploaded }: Model3DUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const { toast } = useToast();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['.glb', '.gltf'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(fileExtension)) {
      toast({
        title: "Ugyldig filtype",
        description: "Kun .glb og .gltf filer er støttet",
        variant: "destructive"
      });
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Fil for stor",
        description: "Maksimal filstørrelse er 10MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('3d-models')
        .upload(fileName, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('3d-models')
        .getPublicUrl(fileName);

      if (urlData?.publicUrl) {
        setUploadedFiles(prev => [...prev, fileName]);
        onModelUploaded?.(urlData.publicUrl);
        toast({
          title: "Opplasting vellykket",
          description: `${file.name} er lastet opp`,
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Opplasting feilet",
        description: "Kunne ikke laste opp filen",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const listStoredModels = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('3d-models')
        .list();

      if (error) throw error;
      
      return data?.map(file => file.name) || [];
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  };

  React.useEffect(() => {
    listStoredModels().then(files => {
      setUploadedFiles(files);
    });
  }, []);

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Last opp 3D-modell
        </CardTitle>
        <CardDescription>
          Støtter .glb og .gltf filer (maks 10MB)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-center w-full">
          <label htmlFor="model-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
              <p className="mb-2 text-sm text-muted-foreground">
                <span className="font-semibold">Klikk for å laste opp</span>
              </p>
              <p className="text-xs text-muted-foreground">GLB eller GLTF</p>
            </div>
            <input
              id="model-upload"
              type="file"
              className="hidden"
              accept=".glb,.gltf"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </label>
        </div>

        {uploading && (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm">Laster opp...</span>
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Opplastede modeller:</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {uploadedFiles.map((fileName) => (
                <div key={fileName} className="flex items-center gap-2 text-xs bg-muted p-2 rounded">
                  <File className="h-3 w-3" />
                  <span className="flex-1 truncate">{fileName}</span>
                  <Check className="h-3 w-3 text-green-500" />
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};